import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { useCopyToClipboard } from '../../../../utils/copy';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Divider from '../../../common/base/Divider';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import DropdownMenu from '../../../common/DropdownMenu';
import { Div } from '../../../common/helpers/StyledUtils';
import { ContextMenuConfig, useCodeMenuOptions } from '../../../common/JSONBrowser';
import Search from '../../../common/Search';
import { CollapsableDataSection } from '../../../common/Data/CollapsableDataSection';
import useLocalStorage from '../../../hooks/useLocalStorage';
const StyledMetadataRow = styled.div<{
  empty: boolean;
}>`
  * {
    display: inline-block;
  }
  ${Text}:first-child {
    white-space: nowrap;
  }
  ${Text}:last-child {
    word-break: break-all;
    font-style: ${({
  empty
}) => empty ? 'italic' : 'normal'};
    color: ${({
  theme,
  empty
}) => empty ? theme.colors.on.neutral.secondary_neutral : theme.colors.on.neutral.primary};
  }
`;
const StyledMetadataRowHover = styled.span<{
  active: boolean;
}>`
  position: relative;
  display: inline-block;
  border-radius: ${({
  theme
}) => theme.radius.small};
  &:hover {
    background-color: ${({
  theme
}) => theme.colors.surface.base.variant_surface};
  }
  ${({
  active,
  theme
}) => active && `
    background-color: ${theme.colors.surface.base.variant_surface};
  `}
  cursor: pointer;
`;
const MetadataRow = t0 => {
  const $ = _c(9);
  const {
    name,
    value,
    context_menu_config,
    children
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = [name];
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = useCodeMenuOptions(name, value, context_menu_config, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      min: 208,
      max: 208
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== value) {
    t3 = (opened, toggle) => <StyledMetadataRowHover active={opened} onClick={() => toggle(true)}><StyledMetadataRow empty={value === ""}>{children}</StyledMetadataRow></StyledMetadataRowHover>;
    $[3] = children;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t3) {
    t4 = <DropdownMenu parent_width={t2} options={options} renderToggle={t3} />;
    $[6] = options;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const HighlightableText = t0 => {
  const $ = _c(9);
  const {
    text,
    search_value
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== search_value || $[1] !== text || $[2] !== theme) {
    let t2;
    if ($[4] !== search_value || $[5] !== theme) {
      t2 = (part, i) => <span key={i} style={part.toLowerCase() === search_value.toLowerCase() ? {
        backgroundColor: theme.colors.surface.container.highlight
      } : {}}>{part}</span>;
      $[4] = search_value;
      $[5] = theme;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = text.split(new RegExp(`(${search_value})`, "i")).map(t2);
    $[0] = search_value;
    $[1] = text;
    $[2] = theme;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <span>{t1}</span>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const MetadataBlock = t0 => {
  const $ = _c(12);
  const {
    metadata,
    search_value,
    context_menu_config
  } = t0;
  let t1;
  if ($[0] !== metadata || $[1] !== search_value) {
    t1 = Object.entries(metadata).filter(t2 => {
      const [key, value] = t2;
      return search_value ? key.toLowerCase().includes(search_value.toLowerCase()) || (value || "empty").toLowerCase().includes(search_value.toLowerCase()) : true;
    });
    $[0] = metadata;
    $[1] = search_value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filtered_entries = t1;
  let t2;
  if ($[3] !== context_menu_config || $[4] !== filtered_entries || $[5] !== search_value) {
    t2 = filtered_entries.map(t3 => {
      const [key_0, value_0] = t3;
      return <MetadataRow key={key_0} name={key_0} value={value_0} context_menu_config={context_menu_config}><Text mono={true} size="s" key={key_0}>{search_value ? <HighlightableText text={key_0} search_value={search_value} /> : key_0}</Text>:{" "}<Text mono={true} size="s">{search_value ? <HighlightableText text={value_0 || "empty"} search_value={search_value} /> : value_0 || "empty"}</Text></MetadataRow>;
    });
    $[3] = context_menu_config;
    $[4] = filtered_entries;
    $[5] = search_value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== filtered_entries.length) {
    t3 = filtered_entries.length === 0 && <Text muted={true} text_wrap={false}>No metadata found</Text>;
    $[7] = filtered_entries.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <Div>{t2}{t3}</Div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
export const OutpostMetadataCollapsibleSection: React.FC<{
  metadata: Record<string, string>;
  embedded: boolean;
  context_menu?: boolean;
  context_menu_config?: ContextMenuConfig | null;
}> = t0 => {
  const $ = _c(52);
  const {
    metadata,
    embedded,
    context_menu: t1,
    context_menu_config: t2
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  const context_menu_config = t2 === undefined ? null : t2;
  const [collapsed, setCollapsed] = useLocalStorage("prefs:outpost:metadata:collapsed", true);
  const theme = useTheme();
  let t3;
  if ($[0] !== metadata) {
    t3 = Object.keys(metadata);
    $[0] = metadata;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const count = t3.length;
  const [search_value, setSearchValue] = useState("");
  const copyToClipboard = useCopyToClipboard();
  let t4;
  if ($[2] !== metadata) {
    t4 = Object.entries(metadata).map(_temp);
    $[2] = metadata;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const metadata_string = t4.join("\n");
  const resolved_config = context_menu ? context_menu_config : null;
  const t5 = embedded ? 0 : collapsed ? 0 : 8;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      b: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = embedded && !collapsed ? theme.colors.surface.base.variant_surface_3 : undefined;
  let t8;
  if ($[6] !== t7) {
    t8 = {
      backgroundColor: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "center",
      gap: 2
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== collapsed || $[10] !== setCollapsed) {
    t10 = () => setCollapsed(!collapsed);
    $[9] = collapsed;
    $[10] = setCollapsed;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Text semi={true}>Metadata</Text>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== count) {
    t12 = count !== undefined && <Badge neutral={true}>{count}</Badge>;
    $[13] = count;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t12) {
    t13 = <Div flex={t9} onClick={t10}>{t11}{t12}</Div>;
    $[15] = t10;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      align: "center",
      gap: 2
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== collapsed || $[20] !== copyToClipboard || $[21] !== embedded || $[22] !== metadata_string) {
    t15 = !collapsed && embedded && <Button ghost={true} icon="copy" onClick={() => copyToClipboard(metadata_string)} />;
    $[19] = collapsed;
    $[20] = copyToClipboard;
    $[21] = embedded;
    $[22] = metadata_string;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== collapsed || $[25] !== embedded || $[26] !== setCollapsed) {
    t16 = embedded ? <Button ghost={true} icon={collapsed ? "chevron_down" : "chevron_up"} onClick={() => setCollapsed(!collapsed)} /> : <Link neutral={true} icon={collapsed ? "chevron_down" : "chevron_up"} onClick={() => setCollapsed(!collapsed)} />;
    $[24] = collapsed;
    $[25] = embedded;
    $[26] = setCollapsed;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t15 || $[29] !== t16) {
    t17 = <Div flex={t14}>{t15}{t16}</Div>;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== collapsed || $[32] !== embedded || $[33] !== t13 || $[34] !== t17) {
    t18 = <CollapsableDataSection embedded={embedded} collapsed={collapsed} z_index={3}>{t13}{t17}</CollapsableDataSection>;
    $[31] = collapsed;
    $[32] = embedded;
    $[33] = t13;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== collapsed || $[37] !== copyToClipboard || $[38] !== embedded || $[39] !== metadata || $[40] !== metadata_string || $[41] !== resolved_config || $[42] !== search_value) {
    t19 = !collapsed && <Div flex={{
      direction: "column",
      gap: 4
    }} p={embedded ? {
      x: 6,
      b: 6
    } : undefined}><Div flex={{
        gap: 3
      }}><Search w={embedded ? 100 : {
          px: 468
        }} small={true} value={search_value} onChange={setSearchValue} />{!embedded && <Button outline={true} icon="copy" onClick={() => copyToClipboard(metadata_string)} />}</Div>{embedded ? <MetadataBlock metadata={metadata} search_value={search_value} context_menu_config={resolved_config} /> : <StyledCard overflow_hidden={true}><StyledCardSection muted={true} p={6}><MetadataBlock metadata={metadata} search_value={search_value} context_menu_config={resolved_config} /></StyledCardSection></StyledCard>}</Div>;
    $[36] = collapsed;
    $[37] = copyToClipboard;
    $[38] = embedded;
    $[39] = metadata;
    $[40] = metadata_string;
    $[41] = resolved_config;
    $[42] = search_value;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== embedded) {
    t20 = embedded && <Divider />;
    $[44] = embedded;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t18 || $[47] !== t19 || $[48] !== t20 || $[49] !== t6 || $[50] !== t8) {
    t21 = <Div m={t6} style={t8}>{t18}{t19}{t20}</Div>;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t6;
    $[50] = t8;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};
function _temp(t0) {
  const [key, value] = t0;
  return `${key}: ${value}`;
}